import type {CreateExtArgs} from '@wix/document-manager-core'
import type {CompRef, Pointer} from '@wix/document-services-types'
import _ from 'lodash'
import {ReportableError} from '@wix/document-manager-utils'
import {isRefPointer} from '../../../../utils/refStructureUtils'
import {getComponentType} from '../../../../utils/dalUtils'
import {COMP_TYPES, VIEW_MODES} from '../../../../constants/constants'
import type {DataModelExtensionAPI} from '../../../dataModel/dataModel'
import type {RelationshipsAPI} from '../../../relationships'
import type {LoggerExtAPI} from '../../../logger'
import type {RefOverridesExtensionAPI} from '../../../refOverrides'

const CONNECTIONS_ITEM_TYPE = 'connections'
const PROPERTIES_ITEM_TYPE = 'props'

const isMobilePointer = (compPointer: Pointer) => compPointer.type === VIEW_MODES.MOBILE

const getAllOverridesToBeRemoved = (
    createExtArgs: CreateExtArgs,
    compRef: CompRef,
    excludeConnectionItems = true,
    excludeNonPropertiesItemsForMobile = false
): Pointer[] => {
    const {dal, pointers, extensionAPI} = createExtArgs
    const {relationships} = extensionAPI as RelationshipsAPI
    const compType = getComponentType(dal, compRef)
    if (compType !== COMP_TYPES.REF_TYPE) {
        return []
    }

    const overrides = _.concat(
        pointers.referredStructure.getAllOverrides(compRef),
        pointers.referredStructure.getAllTranslationOverrides(compRef)
    )
    return _(overrides)
        .filter(override =>
            isMobilePointer(compRef) && excludeNonPropertiesItemsForMobile
                ? override.type === PROPERTIES_ITEM_TYPE
                : true
        )
        .reject(override => (excludeConnectionItems ? override.type === CONNECTIONS_ITEM_TYPE : false))
        .reject(override => relationships.getOwningReferencesToPointer(override).length > 0)
        .map(pointer => pointers.referredStructure.getPointerWithoutFallbacks(pointer))
        .value()
}

export const saveOverrides = (createExtArgs: CreateExtArgs, compPointer: CompRef) => {
    const {refOverrides} = createExtArgs.extensionAPI as RefOverridesExtensionAPI
    const overrides = getAllOverridesToBeRemoved(createExtArgs, compPointer, false, true)
    refOverrides.componentRemovalOverrides.save(compPointer, overrides)
}

export const removeAllOverrides = (createExtArgs: CreateExtArgs, compPointer: Pointer) => {
    const {extensionAPI} = createExtArgs
    const {dataModel} = extensionAPI as DataModelExtensionAPI
    const {refOverrides} = extensionAPI as RefOverridesExtensionAPI
    const overrides = refOverrides.componentRemovalOverrides.get(compPointer)
    overrides.forEach(pointer => dataModel.removeItemRecursively(pointer))
    refOverrides.componentRemovalOverrides.clear(compPointer)
}

export const removeInternalRefOverrides = (createExtArgs: CreateExtArgs, compPointer: Pointer) => {
    const {pointers, extensionAPI} = createExtArgs
    const {dataModel} = extensionAPI as DataModelExtensionAPI
    const {logger} = extensionAPI as LoggerExtAPI
    if (isRefPointer(compPointer)) {
        logger.captureError(
            new ReportableError({
                errorType: 'AttemptToRemoveRemoteStructure',
                message: `attempt to remove ${compPointer?.id}`
            })
        )
        return
    }
    const overrides = pointers.referredStructure.getOverridesTargetingComp(compPointer)
    overrides.forEach(pointer => dataModel.removeItemRecursively(pointer))
}
